import { useEffect, useRef, useState } from 'preact/hooks'
import { emailActivityApi } from '@/api/emailActivity'
import type { WorkspaceEmailActivityDto } from '@/types/emailActivity'

export const EMAIL_ACTIVITY_PAGE_SIZE = 25

interface UseWorkspaceEmailActivityOptions {
    workspaceId: string
    status: string
    type?: string
    search: string
    page: number
}

// Loads one page of the workspace email-activity list, re-fetching when the filters / search / page
// change. A generation token (requestIdRef) drops superseded responses, and the in-flight request is
// aborted on change, so rapid filter changes can't race a stale render. Mirrors the ORB-384 hook shape.
export function useWorkspaceEmailActivity({ workspaceId, status, type, search, page }: UseWorkspaceEmailActivityOptions) {
    const [data, setData] = useState<WorkspaceEmailActivityDto | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [reloadKey, setReloadKey] = useState(0)
    const requestIdRef = useRef(0)

    useEffect(() => {
        const requestId = ++requestIdRef.current
        const controller = new AbortController()
        setLoading(true)
        setError(null)

        void emailActivityApi
            .list(workspaceId, {
                status,
                type: type || undefined,
                search: search.trim() || undefined,
                skip: page * EMAIL_ACTIVITY_PAGE_SIZE,
                take: EMAIL_ACTIVITY_PAGE_SIZE,
                signal: controller.signal,
            })
            .then(res => {
                if (requestIdRef.current !== requestId) return
                if (res.error) setError(res.error)
                else setData(res.data ?? null)
                setLoading(false)
            })

        return () => {
            requestIdRef.current++
            controller.abort()
        }
    }, [workspaceId, status, type, search, page, reloadKey])

    return {
        data,
        loading,
        error,
        pageSize: EMAIL_ACTIVITY_PAGE_SIZE,
        reload: () => setReloadKey(key => key + 1),
    }
}
